import clsx from 'clsx';
import { AlertTriangle, ArrowUpRight, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { enumLabel, useI18n, type TranslationKey } from '../lib/i18n';
import type { Period } from '../lib/period';
import type { AiView, Confidence, ScoreLabel, Signal } from '../lib/types';
import { Badge, priorityTone, ProgressBar, type BadgeTone } from './ui';
import { Delta, KpiCard, MoneyValue, NotMeasurable, Section, useMoney, useNumber } from './kit';
import { hrefWith, useDay, usePercent } from './a-common';
import { lineOf, type ClientRef, type ExpiringContract, type FinanceOverview, type OverdueInvoice, type ProjectRef } from './a-finance';
import { CompareBars } from './a-charts';

/**
 * The Executive Overview's building blocks (CR-008 §16.2) — the same figures whether the overview is read
 * live or from a stored report: its top-line figures, company health with its pillars, the deterministic
 * risks and positives worded from their codes, the work against the period before, and what needs
 * attention now. Every link keeps the period it was read for.
 */

// ---- shapes (apps/backend/src/modules/executive/executive-overview.service.ts) -----------------------

export interface SlaFigure {
  decided: number;
  onTime: number;
  compliancePercent: number | null;
}

export interface WorkOverview {
  tasks: {
    created: number;
    completed: number;
    uniqueRootItemsCompleted: number;
    workflowStepsCompleted: number;
    fromQTasks: number;
    cancelled: number;
    open: { toDo: number; inProgress: number; blocked: number; review: number };
    overdue: number;
    dueSoon: number;
    cycleTime: { basis: string; sample: number; medianHours: number | null; p90Hours: number | null };
  };
  tickets: {
    created: number;
    resolvedTickets: number;
    resolutionEvents: number;
    reopenEvents: number;
    closed: number;
    open: Record<string, number>;
    urgentOpen: number;
    highOpen: number;
    urgentOrHighUnassigned: number;
    unassignedPastDeadline: number;
    openViolations: number;
    viewSla: SlaFigure;
    assignmentSla: SlaFigure;
    reopenRatePercent: number | null;
  };
  qTasks: { available: number; claimed: number; inProgress: number; blocked: number; doneStandalone: number; converted: number; skipped: number; queueAgeMedianHours: number | null; blockedLongCount: number };
  workflows: { active: number; completed: number; stopped: number; stepsCompleted: number; stalledCount: number };
  leaderTasks: { open: number; done: number };
}

export interface PeopleSummary {
  people: number;
  scored: number;
  notEnoughEvidence: number;
  coveragePercent: number | null;
  medianScore: number | null;
  byLabel: Record<ScoreLabel, number>;
}

export interface PeopleRow {
  userId: string;
  departmentIds: string[];
  score: number | null;
  label: ScoreLabel | null;
  reason: string | null;
  confidence: Confidence;
  evidenceCount: number;
  measurableWeight: number;
  status: string;
}

export interface HealthPillar {
  key: string;
  weight: number;
  score: number | null;
  measurable: boolean;
  components: Array<{ key: string; value: number | null; score: number | null }>;
  reason: string | null;
}

export interface Health {
  version?: string;
  score: number | null;
  label: ScoreLabel | null;
  measurableWeight: number;
  reason: string | null;
  pillars: HealthPillar[];
}

export interface ProjectBrief {
  id: string;
  code: string;
  name: string;
  nameAr: string | null;
  status: string;
  expectedEndDate?: string | null;
}

export interface UrgentTicket {
  id: string;
  number: string;
  subject: string;
  priority: string;
  status: string;
  createdAt: string;
  assignmentDeadlineAt: string | null;
  assignedUserId: string | null;
  client: ClientRef | null;
  targetDepartment: { id: string; name: string; nameAr: string | null } | null;
}

export interface Urgent {
  overdueInvoices: OverdueInvoice[];
  urgentTickets: UrgentTicket[];
  expiringContracts: ExpiringContract[];
  atRiskProjects: Array<{ project: ProjectBrief; risk: { tier: string; reasons: string[] } }>;
}

/** The overview as computed — what `GET /executive/overview` answers and what a report stores. */
export interface OverviewData {
  period: Period;
  asOf: string;
  status: string;
  definitions?: { work: string; health: string; risk: string };
  finance: FinanceOverview;
  work: WorkOverview;
  workPrevious: { completed: number; created: number; ticketsCreated: number };
  people: { summary: PeopleSummary; truncated: boolean; rows: PeopleRow[] };
  projects: { counts: { total: number; active: number; support: number; atRisk: number; watch: number }; atRisk: Array<{ project: ProjectBrief; client: ClientRef | null }> };
  contracts: { expiring: ExpiringContract[]; dueNotInvoiced: number };
  health: Health;
  risks: Signal[];
  positives: Signal[];
  metrics: Array<{ id: string; value: number | string | null; unit: string; basis?: string; complete: boolean }>;
  urgent: Urgent;
}

export interface OverviewResponse extends OverviewData {
  settingsVersion: number;
  snapshot: { id: string; status: string; revision: number; computedAt: string } | null;
  ai: AiView;
}

// ---- helpers -----------------------------------------------------------------------------------------

/** Where a figure AI cites can be read: its page, for the same period. */
export function evidenceHref(id: string, query: string): string | null {
  if (id.startsWith('metric:finance.receivable')) return hrefWith('/finance', query, { tab: 'receivables' });
  if (id === 'metric:finance.expenses' || id === 'metric:finance.expensesPrevious') return hrefWith('/finance', query, { tab: 'expenses' });
  if (id === 'metric:finance.trust') return hrefWith('/finance', query, { tab: 'trust' });
  if (id.startsWith('metric:finance.')) return hrefWith('/finance', query);
  if (/^metric:(work|tickets|qTasks|workflows)\./.test(id)) return hrefWith('/work', query);
  if (id.startsWith('metric:projects.')) return hrefWith('/projects', query);
  if (/^metric:(contracts|subscriptions)\./.test(id)) return hrefWith('/finance', query, { tab: 'contracts' });
  if (id.startsWith('metric:people.')) return hrefWith('/employees', query);
  return null;
}

/** The comparison's basis in words: "the month before", "the same days of last month"… */
export function useBasisText() {
  const { t, has } = useI18n();
  return (basis: string | null | undefined) => {
    const key = `period.basis.${basis ?? ''}`;
    return has(key) ? t(key) : (basis ?? '');
  };
}

const LABEL_TONE: Record<string, BadgeTone> = { EXCELLENT: 'success', VERY_GOOD: 'info', GOOD: 'neutral', NEEDS_ATTENTION: 'warning' };

/** A score's label (Excellent … Needs attention) in words. */
export function ScoreLabelBadge({ label }: { label: string | null | undefined }) {
  const { t, has } = useI18n();
  if (!label) return null;
  const key = `health.label.${label}`;
  return <Badge tone={LABEL_TONE[label] ?? 'neutral'}>{has(key) ? t(key) : label}</Badge>;
}

const MONEY_PARAMS: Record<string, string[]> = {
  NEGATIVE_NET_RESULT: ['net'],
  POSITIVE_NET_RESULT: ['net'],
  REVENUE_UP: ['now', 'before'],
  EXPENSE_SPIKE: ['now', 'before'],
};
const PERCENT_PARAMS: Record<string, string[]> = {
  SLA_STRONG: ['view', 'assignment'],
  RECEIVABLES_OVERDUE: ['share'],
  RECEIVABLE_CONCENTRATION: ['share'],
  EXPENSE_SPIKE: ['threshold'],
};

/** A risk or a positive in words, from its code and the figures it read (executive-health.ts `signals`). */
export function useSignalText() {
  const { t, has } = useI18n();
  const money = useMoney();
  const num = useNumber();
  const pct = usePercent();
  return (kind: 'risk' | 'positive', signal: Signal, currency: string) => {
    const vars: Record<string, string> = {};
    for (const [k, v] of Object.entries(signal.params ?? {})) {
      if (v === null || v === undefined) vars[k] = t('common.unknown');
      else if (MONEY_PARAMS[signal.key]?.includes(k)) vars[k] = money(Number(v), currency);
      else if (PERCENT_PARAMS[signal.key]?.includes(k)) vars[k] = pct(Number(v));
      else if (k === 'status') {
        const key = `trust.${v}`;
        vars[k] = has(key) ? t(key) : String(v);
      } else vars[k] = typeof v === 'number' ? num(v) : String(v);
    }
    const key = `${kind}.${signal.key}`;
    return has(key) ? t(key, vars) : t('risk.unknown', { key: signal.key });
  };
}

const SIGNAL_LINK: Record<string, { path: string; tab?: string }> = {
  OVERDUE_GROWING: { path: '/work' },
  OVERDUE_FALLING: { path: '/work' },
  URGENT_TICKETS_UNASSIGNED: { path: '/work' },
  SLA_BREACHES_OPEN: { path: '/work' },
  Q_TASKS_BLOCKED_LONG: { path: '/work' },
  WORKFLOWS_STALLED: { path: '/work' },
  SLA_STRONG: { path: '/work' },
  THROUGHPUT_UP: { path: '/work' },
  FINANCE_NOT_VERIFIED: { path: '/finance', tab: 'trust' },
  NEGATIVE_NET_RESULT: { path: '/finance' },
  POSITIVE_NET_RESULT: { path: '/finance' },
  REVENUE_UP: { path: '/finance' },
  EXPENSE_SPIKE: { path: '/finance', tab: 'expenses' },
  RECEIVABLES_OVERDUE: { path: '/finance', tab: 'receivables' },
  RECEIVABLE_CONCENTRATION: { path: '/finance', tab: 'receivables' },
  CONTRACTS_EXPIRING: { path: '/finance', tab: 'contracts' },
  CONTRACT_PERIODS_NOT_INVOICED: { path: '/finance', tab: 'contracts' },
  SUBSCRIPTIONS_PAST_DUE: { path: '/finance', tab: 'contracts' },
  PROJECTS_AT_RISK: { path: '/projects' },
};

const SEVERITY_TONE: Record<string, BadgeTone> = { HIGH: 'error', MEDIUM: 'warning', LOW: 'neutral' };

/** "+12%" / "−5%" between two counts, uncoloured — for figures that are neither good nor bad going up. */
function useCountChange() {
  const num = useNumber();
  return (now: number, before: number) => {
    if (before === 0) return null;
    const pct = Math.round(((now - before) / before) * 1000) / 10;
    return `${pct > 0 ? '+' : pct < 0 ? '−' : '±'}${num(Math.abs(pct), 1)}%`;
  };
}

// ---- blocks ------------------------------------------------------------------------------------------

/** The overview's top-line figures, each linking to the page that explains it. */
export function OverviewKpis({ data, query }: { data: OverviewData; query: string }) {
  const { t } = useI18n();
  const money = useMoney();
  const num = useNumber();
  const pct = usePercent();
  const day = useDay();
  const f = data.finance;
  const base = f.context.baseCurrency;
  const basis = data.period.comparison.basis;
  const asMoney = (n: number) => money(n, base);
  const rec = f.cards.receivables;
  const people = data.people.summary;
  const counts = data.projects.counts;
  const delivering = counts.active + counts.support;
  const health = data.health;
  const net = f.cards.netResult.current;

  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
      <KpiCard
        label={t('metric.revenue')}
        value={<MoneyValue line={f.cards.revenue.current} />}
        delta={<Delta current={f.cards.revenue.current.base} previous={f.cards.revenue.previous.base} basis={basis} format={asMoney} />}
        hint={t('metric.revenueHint')}
        to={hrefWith('/finance', query, { tab: null })}
      />
      <KpiCard
        label={t('metric.netResult')}
        value={<MoneyValue line={net} />}
        delta={<Delta current={net.base} previous={f.cards.netResult.previous.base} basis={basis} format={asMoney} />}
        hint={t('metric.netResultHint')}
        tone={net.base !== null && net.base < 0 ? 'error' : 'neutral'}
        to={hrefWith('/finance', query, { tab: null })}
      />
      <KpiCard
        label={t('metric.invoicesIssued')}
        value={<MoneyValue line={f.cards.invoicesIssued.current} />}
        delta={<Delta current={f.cards.invoicesIssued.current.base} previous={f.cards.invoicesIssued.previous.base} basis={basis} format={asMoney} />}
        hint={t('metric.invoicesIssuedHint')}
        to={hrefWith('/finance', query, { tab: null })}
      />
      <KpiCard
        label={t('metric.operatingExpenses')}
        value={<MoneyValue line={f.cards.operatingExpenses.current} />}
        delta={<Delta current={f.cards.operatingExpenses.current.base} previous={f.cards.operatingExpenses.previous.base} basis={basis} format={asMoney} goodWhenDown />}
        hint={t('metric.operatingExpensesHint')}
        to={hrefWith('/finance', query, { tab: 'expenses' })}
      />
      <KpiCard
        label={t('metric.receivables')}
        value={<MoneyValue line={lineOf(rec.total, rec.currency, rec.complete, rec.unconverted)} />}
        hint={
          rec.complete && rec.total === 0
            ? t('metric.nothingOwed', { date: day(rec.asOf) })
            : t('metric.receivablesHint', { date: day(rec.asOf), overdue: rec.complete ? money(rec.overdue, rec.currency) : t('state.partial'), share: pct(rec.overdueSharePercent) })
        }
        tone={rec.overdueSharePercent !== null && rec.overdueSharePercent >= 50 ? 'warning' : 'neutral'}
        to={hrefWith('/finance', query, { tab: 'receivables' })}
      />
      <KpiCard
        label={t('metric.workCoverage')}
        value={
          people.people > 0 ? (
            <span className="inline-flex flex-wrap items-baseline gap-x-2">
              {num(people.scored)}
              <span className="text-body font-normal text-muted">{t('metric.scoredOf', { people: num(people.people) })}</span>
            </span>
          ) : (
            '—'
          )
        }
        hint={
          <span className="flex flex-col gap-0.5">
            <span>{t('metric.workCoverageHint', { none: num(people.notEnoughEvidence), median: people.medianScore === null ? '—' : num(people.medianScore, 0) })}</span>
            {data.people.truncated && <span className="text-warning-fg dark:text-warning-dark">{t('state.truncated')}</span>}
          </span>
        }
        tone={data.people.truncated ? 'warning' : 'neutral'}
        to={hrefWith('/employees', query)}
      />
      <KpiCard
        label={t('metric.atRiskProjects')}
        value={num(counts.atRisk)}
        hint={t('metric.atRiskHint', { of: num(delivering), watch: num(counts.watch) })}
        tone={counts.atRisk > 0 ? 'warning' : 'neutral'}
        to={hrefWith('/projects', query)}
      />
      <KpiCard
        label={t('metric.health')}
        value={
          health.score === null ? (
            <span className="text-title text-muted">{t('health.notEnoughData')}</span>
          ) : (
            <span className="inline-flex flex-wrap items-center gap-2">
              {num(health.score, 0)}
              <ScoreLabelBadge label={health.label} />
            </span>
          )
        }
        hint={t('metric.healthHint', { weight: num(health.measurableWeight, 1) })}
      />
    </div>
  );
}

const PILLAR_KEYS = new Set(['financial', 'work', 'delivery', 'service']);

/** Company health: the composite when enough of the weight is measurable, and every pillar with why. */
export function HealthCard({ health, className }: { health: Health; className?: string }) {
  const { t, has } = useI18n();
  const num = useNumber();
  const reasonText = (reason: string | null) => {
    if (!reason) return null;
    const key = `health.reason.${reason}`;
    return has(key) ? t(key) : reason;
  };
  const componentText = (c: { key: string; value: number | null }) => {
    const key = `health.componentValue.${c.key}`;
    return c.value === null || !has(key) ? null : t(key, { value: num(c.value, 1) });
  };
  return (
    <Section title={t('health.title')} subtitle={t('health.subtitle')} className={className}>
      <div id="health" className="flex flex-wrap items-center gap-3">
        {health.score === null ? (
          <div>
            <p className="text-title text-[var(--text-primary)]">{t('health.notEnoughData')}</p>
            {health.reason && <p className="mt-0.5 text-caption text-muted">{reasonText(health.reason)}</p>}
          </div>
        ) : (
          <>
            <span className="text-metric tabular text-[var(--text-primary)]">{num(health.score, 0)}</span>
            <ScoreLabelBadge label={health.label} />
          </>
        )}
      </div>
      <p className="mt-1 text-caption text-muted">{t('health.measurableWeight', { weight: num(health.measurableWeight, 1) })}</p>
      <ul className="mt-4 space-y-4">
        {health.pillars.map((p) => (
          <li key={p.key}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-body-sm font-medium text-[var(--text-primary)]">
                {PILLAR_KEYS.has(p.key) ? t(`health.pillar.${p.key as 'financial' | 'work' | 'delivery' | 'service'}`) : p.key}
                <span className="ms-2 text-caption font-normal text-[var(--text-tertiary)]">{t('health.weight', { weight: num(p.weight) })}</span>
              </span>
              <span className="tabular text-body-sm">{p.score === null ? <NotMeasurable compact reason="INSUFFICIENT_COVERAGE" /> : num(p.score, 0)}</span>
            </div>
            {p.score !== null && (
              <div className="mt-1.5">
                <ProgressBar percent={p.score} />
              </div>
            )}
            {p.reason && <p className="mt-1 text-caption text-muted">{reasonText(p.reason)}</p>}
            {p.components.length > 0 && (
              <ul className="mt-1.5 space-y-0.5 text-caption text-muted">
                {p.components.map((c) => {
                  const nameKey = `health.component.${c.key}`;
                  const value = componentText(c);
                  return (
                    <li key={c.key} className="flex flex-wrap justify-between gap-x-3">
                      <span>
                        {has(nameKey) ? t(nameKey) : c.key}
                        {value && <span className="text-[var(--text-tertiary)]"> · {value}</span>}
                      </span>
                      <span className="tabular">{c.score === null ? t('state.notMeasurable') : num(c.score, 0)}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Section>
  );
}

/** The risk rules that fired, and the positive ones — in words, each linking to where it can be read. */
export function SignalsCard({ risks, positives, currency, query, className }: { risks: Signal[]; positives: Signal[]; currency: string; query: string; className?: string }) {
  const { t, has } = useI18n();
  const text = useSignalText();
  const item = (kind: 'risk' | 'positive', s: Signal) => {
    const link = SIGNAL_LINK[s.key];
    const body = text(kind, s, currency);
    return (
      <li key={s.id} className="flex items-start gap-2 text-body-sm">
        {kind === 'risk' ? (
          <AlertTriangle className={clsx('mt-0.5 h-4 w-4 shrink-0', s.severity === 'HIGH' ? 'text-error-fg dark:text-error-dark' : 'text-warning-fg dark:text-warning-dark')} aria-hidden />
        ) : (
          <ArrowUpRight className="mt-0.5 h-4 w-4 shrink-0 text-success-fg dark:text-success flip-rtl" aria-hidden />
        )}
        <span className="min-w-0 flex-1">
          {link ? (
            <Link to={hrefWith(link.path, query, link.tab ? { tab: link.tab } : undefined)} className="hover:underline">
              {body}
            </Link>
          ) : (
            body
          )}
        </span>
        {kind === 'risk' && s.severity && (
          <Badge tone={SEVERITY_TONE[s.severity] ?? 'neutral'}>{has(`risk.severity.${s.severity}`) ? t(`risk.severity.${s.severity}`) : s.severity}</Badge>
        )}
      </li>
    );
  };
  return (
    <Section title={t('risk.sectionTitle')} subtitle={t('risk.rulesNote')} className={className}>
      <div className="grid gap-6 lg:grid-cols-2">
        <div>
          <h4 className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{t('risk.title')}</h4>
          {risks.length ? <ul className="space-y-2">{risks.map((s) => item('risk', s))}</ul> : <p className="text-body-sm text-muted">{t('risk.none')}</p>}
        </div>
        <div>
          <h4 className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{t('positive.title')}</h4>
          {positives.length ? <ul className="space-y-2">{positives.map((s) => item('positive', s))}</ul> : <p className="text-body-sm text-muted">{t('positive.none')}</p>}
        </div>
      </div>
    </Section>
  );
}

/** The work of the period against the period before, each work item counted once. */
export function WorkSummaryCard({ work, previous, basis, query, className }: { work: WorkOverview; previous: OverviewData['workPrevious']; basis: string; query: string; className?: string }) {
  const { t } = useI18n();
  const num = useNumber();
  const pct = usePercent();
  const basisText = useBasisText();
  const change = useCountChange();
  const neutral = (now: number, before: number) => {
    const c = change(now, before);
    return c ? <span className="text-caption tabular text-muted">{c}</span> : null;
  };
  const sla = (s: SlaFigure) => (s.decided > 0 ? t('overview.work.slaValue', { percent: pct(s.compliancePercent), decided: num(s.decided) }) : t('overview.work.noSla'));
  const facts: Array<{ key: string; label: TranslationKey; value: ReactNode; warn?: boolean }> = [
    { key: 'steps', label: 'overview.work.steps', value: num(work.tasks.workflowStepsCompleted) },
    { key: 'resolved', label: 'overview.work.resolved', value: num(work.tickets.resolvedTickets) },
    { key: 'overdue', label: 'overview.work.overdueNow', value: num(work.tasks.overdue), warn: work.tasks.overdue > 0 },
    { key: 'dueSoon', label: 'overview.work.dueSoon', value: num(work.tasks.dueSoon) },
    { key: 'viewSla', label: 'overview.work.viewSla', value: sla(work.tickets.viewSla) },
    { key: 'assignmentSla', label: 'overview.work.assignmentSla', value: sla(work.tickets.assignmentSla) },
    { key: 'reopen', label: 'overview.work.reopenRate', value: pct(work.tickets.reopenRatePercent) },
    { key: 'urgentOpen', label: 'overview.work.urgentOpen', value: t('overview.work.urgentHigh', { urgent: num(work.tickets.urgentOpen), high: num(work.tickets.highOpen) }) },
  ];
  return (
    <Section
      title={t('overview.work.title')}
      subtitle={t('overview.work.subtitle', { basis: basisText(basis) })}
      className={className}
      action={
        <Link to={hrefWith('/work', query)} className="inline-flex items-center gap-1 text-body-sm text-primary-600 hover:underline dark:text-primary-400">
          {t('overview.work.open')}
          <ChevronRight className="h-4 w-4 flip-rtl" aria-hidden />
        </Link>
      }
    >
      <CompareBars
        currentLabel={t('overview.work.now')}
        previousLabel={t('overview.work.before')}
        rows={[
          {
            key: 'completed',
            label: t('overview.work.completed'),
            now: work.tasks.uniqueRootItemsCompleted,
            before: previous.completed,
            change: <Delta current={work.tasks.uniqueRootItemsCompleted} previous={previous.completed} basis={basis} />,
          },
          { key: 'created', label: t('overview.work.created'), now: work.tasks.created, before: previous.created, change: neutral(work.tasks.created, previous.created) },
          { key: 'tickets', label: t('overview.work.ticketsCreated'), now: work.tickets.created, before: previous.ticketsCreated, change: neutral(work.tickets.created, previous.ticketsCreated) },
        ]}
      />
      <p className="mt-2 text-caption text-muted">{t('overview.work.completedHint')}</p>
      <dl className="mt-4 grid gap-x-4 gap-y-3 border-t hairline pt-4 sm:grid-cols-2">
        {facts.map((f) => (
          <div key={f.key} className="flex items-baseline justify-between gap-3">
            <dt className="text-body-sm text-[var(--text-tertiary)]">{t(f.label)}</dt>
            <dd className={clsx('text-body-sm tabular text-end', f.warn ? 'text-warning-fg dark:text-warning-dark' : 'text-[var(--text-primary)]')}>{f.value}</dd>
          </div>
        ))}
      </dl>
    </Section>
  );
}

function UrgentList({ title, action, empty, children, count }: { title: ReactNode; action?: ReactNode; empty: boolean; children: ReactNode; count: number }) {
  const { t } = useI18n();
  return (
    <Section
      title={
        <span className="inline-flex items-center gap-2">
          {title}
          {count > 0 && <Badge tone="warning">{count}</Badge>}
        </span>
      }
      action={action}
    >
      {empty ? <p className="text-body-sm text-muted">{t('overview.urgent.none')}</p> : <ul className="divide-y divide-[var(--line)]">{children}</ul>}
    </Section>
  );
}

/** What needs attention now: overdue invoices, urgent tickets, contracts ending, projects at risk. */
export function UrgentSection({ urgent, query }: { urgent: Urgent | null | undefined; query: string }) {
  const { t, has, localized, locale } = useI18n();
  const money = useMoney();
  const num = useNumber();
  const day = useDay();
  const invoices = urgent?.overdueInvoices ?? [];
  const tickets = urgent?.urgentTickets ?? [];
  const contracts = urgent?.expiringContracts ?? [];
  const projects = urgent?.atRiskProjects ?? [];
  const reason = (r: string) => {
    const key = `overview.reason.${r}`;
    return has(key) ? t(key) : r;
  };
  const linkClass = 'text-primary-600 hover:underline dark:text-primary-400';
  return (
    <section aria-labelledby="urgent-title">
      <h2 id="urgent-title" className="mb-3 text-h2 text-[var(--text-primary)]">
        {t('overview.urgent.title')}
      </h2>
      <div className="grid gap-4 lg:grid-cols-2">
        <UrgentList
          title={t('overview.urgent.overdueInvoices')}
          count={invoices.length}
          empty={invoices.length === 0}
          action={
            <Link to={hrefWith('/finance', query, { tab: 'receivables' })} className={clsx('text-body-sm', linkClass)}>
              {t('overview.urgent.allReceivables')}
            </Link>
          }
        >
          {invoices.map((inv) => (
            <li key={inv.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-body-sm">
              <span className="min-w-0">
                <Link to={hrefWith('/finance', query, { tab: 'receivables' })} className={clsx('font-mono text-mono-sm', linkClass)} dir="ltr">
                  {inv.number}
                </Link>
                {inv.client && (
                  <Link to={hrefWith(`/clients/${inv.client.id}`, query)} className="ms-2 text-[var(--text-secondary)] hover:underline">
                    {localized(inv.client)}
                  </Link>
                )}
                <span className="ms-2 text-caption text-muted">{t('overview.urgent.daysOverdue', { n: num(inv.daysOverdue) })}</span>
              </span>
              <span className="tabular text-error-fg dark:text-error-dark">{money(inv.remaining, inv.currency)}</span>
            </li>
          ))}
        </UrgentList>

        <UrgentList title={t('overview.urgent.urgentTickets')} count={tickets.length} empty={tickets.length === 0}>
          {tickets.map((tk) => (
            <li key={tk.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-body-sm">
              <span className="min-w-0">
                <Link to={hrefWith(`/work/evidence/ticket/${tk.id}`, query)} className={linkClass}>
                  <span className="font-mono text-mono-sm" dir="ltr">
                    {tk.number}
                  </span>
                  <span className="ms-2">{tk.subject}</span>
                </Link>
                <span className="ms-2 text-caption text-muted">
                  {tk.client ? localized(tk.client) : t('common.internal')}
                  {!tk.assignedUserId && <> · {t('overview.urgent.unassigned')}</>}
                </span>
              </span>
              <Badge tone={priorityTone(tk.priority)}>{enumLabel(tk.priority, locale)}</Badge>
            </li>
          ))}
        </UrgentList>

        <UrgentList
          title={t('overview.urgent.expiringContracts')}
          count={contracts.length}
          empty={contracts.length === 0}
          action={
            <Link to={hrefWith('/finance', query, { tab: 'contracts' })} className={clsx('text-body-sm', linkClass)}>
              {t('overview.urgent.allContracts')}
            </Link>
          }
        >
          {contracts.map((c) => (
            <li key={c.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-body-sm">
              <span className="min-w-0">
                <span className="font-mono text-mono-sm text-[var(--text-secondary)]" dir="ltr">
                  {c.number}
                </span>
                <span className="ms-2">{c.title}</span>
                {c.client && (
                  <Link to={hrefWith(`/clients/${c.client.id}`, query)} className="ms-2 text-caption text-muted hover:underline">
                    {localized(c.client)}
                  </Link>
                )}
              </span>
              <span className="text-caption text-muted">{t('overview.urgent.endsIn', { date: day(c.endDate), n: num(c.daysLeft) })}</span>
            </li>
          ))}
        </UrgentList>

        <UrgentList title={t('overview.urgent.atRiskProjects')} count={projects.length} empty={projects.length === 0}>
          {projects.map(({ project, risk }) => (
            <li key={project.id} className="py-2 text-body-sm">
              <Link to={hrefWith(`/projects/${project.id}`, query)} className={linkClass}>
                <span className="font-mono text-mono-sm" dir="ltr">
                  {project.code}
                </span>
                <span className="ms-2">{localized(project)}</span>
              </Link>
              {risk.reasons.length > 0 && <p className="mt-0.5 text-caption text-muted">{risk.reasons.map(reason).join(' · ')}</p>}
            </li>
          ))}
        </UrgentList>
      </div>
    </section>
  );
}

export type { ProjectRef };
